import React from 'react'
import { Button, Icon, Upload } from 'antd'
import './BrandChoicePanel.scss'

const { Dragger } = Upload

const BrandChoicePanel = ({
  disabled,
  acceptedExtensions,
  hasCompletedProfile,
  onUploadGuidelines,
  onStartFromScratch,
  onViewProfile,
  onGoToVideoList
}) => {
  const uploadProps = {
    multiple: false,
    showUploadList: false,
    accept: acceptedExtensions.map(extension => `.${extension}`).join(','),
    beforeUpload: file => {
      onUploadGuidelines(file)
      return false
    },
    disabled
  }

  return (
    <div className="brand-choice-panel">
      <div className="brand-choice-panel__rail">
        <div className="brand-choice-panel__rail-heading">
          <span>Setup path</span>
          <strong>3 quick steps</strong>
        </div>
        <div className="brand-choice-panel__rail-list">
          <div className="brand-choice-panel__rail-item brand-choice-panel__rail-item--active">
            <Icon type="profile" />
            <div>
              <strong>Choose input</strong>
              <span>Upload or start fresh</span>
            </div>
          </div>
          <div className="brand-choice-panel__rail-item">
            <Icon type="message" />
            <div>
              <strong>Answer gaps</strong>
              <span>Short guided questions</span>
            </div>
          </div>
          <div className="brand-choice-panel__rail-item">
            <Icon type="check-circle" />
            <div>
              <strong>Finalize</strong>
              <span>Apply to AI video</span>
            </div>
          </div>
        </div>

        {hasCompletedProfile ? (
          <div className="brand-choice-panel__completed-actions">
            <div>
              <span>Profile ready</span>
              <strong>Use or review your completed brand profile.</strong>
            </div>
            <Button
              type="primary"
              block
              icon="eye"
              disabled={disabled}
              onClick={onViewProfile}
            >
              View profile
            </Button>
            {onGoToVideoList ? (
              <Button
                block
                icon="unordered-list"
                disabled={disabled}
                onClick={onGoToVideoList}
              >
                Continue to AI video list
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="brand-choice-panel__main">
        <div className="brand-choice-panel__intro">
          <div className="brand-choice-panel__eyebrow">AI video setup</div>
          <h2>Choose your brand setup path</h2>
          <p>
            Upload existing guidelines or answer a few questions. Sharelook
            turns either path into a video-ready brand profile.
          </p>
        </div>

        <div className="brand-choice-panel__actions">
          <div className="brand-choice-panel__option brand-choice-panel__option--upload">
            <div className="brand-choice-panel__option-top">
              <span className="brand-choice-panel__option-label">Option 1</span>
              <div className="brand-choice-panel__option-icon">
                <Icon type="inbox" />
              </div>
            </div>
            <h3>Upload existing guidelines</h3>
            <p>
              Use a PDF, deck, document, logo guide, or brand notes.
            </p>
            <div className="brand-choice-panel__upload">
              <Dragger {...uploadProps}>
                <div className="brand-choice-panel__upload-action">
                  <Icon type="upload" />
                  <span>Drop file here or browse</span>
                </div>
                <div className="brand-choice-panel__upload-copy">
                  PDF, DOCX, PPTX, PNG, JPG, SVG, TXT, or MD up to 50 MB
                </div>
              </Dragger>
            </div>
          </div>

          <div className="brand-choice-panel__option brand-choice-panel__option--scratch">
            <div className="brand-choice-panel__option-top">
              <span className="brand-choice-panel__option-label">Option 2</span>
              <div className="brand-choice-panel__option-icon">
                <Icon type="edit" />
              </div>
            </div>
            <h3>Create from scratch</h3>
            <p>
              Let the assistant define your voice, visuals, and video style.
            </p>
            <div className="brand-choice-panel__scratch-note">
              <Icon type="question-circle" />
              <span>
                Answer naturally. One question set at a time.
              </span>
            </div>
            <Button
              type="primary"
              disabled={disabled}
              onClick={onStartFromScratch}
            >
              Start discovery
              <Icon type="arrow-right" />
            </Button>
          </div>

          <div className="brand-choice-panel__option brand-choice-panel__option--skip">
            <div className="brand-choice-panel__option-top">
              <span className="brand-choice-panel__option-label">Option 3</span>
              <div className="brand-choice-panel__option-icon">
                <Icon type="forward" />
              </div>
            </div>
            <h3>Skip onboarding</h3>
            <p>
              Continue directly to create videos without setting up brand guidelines.
            </p>
            <div className="brand-choice-panel__scratch-note">
              <Icon type="info-circle" />
              <span>
                You can set up or modify your brand profile at any time.
              </span>
            </div>
            <Button
              disabled={disabled}
              onClick={onGoToVideoList}
            >
              Continue to videos
              <Icon type="arrow-right" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrandChoicePanel
